import { Injectable } from "@angular/core";
import { PAYLOAD_PERSON_TYPES, EXCEL_PERSON_TYPES, MassUploadStateFormMap, EXCEL_TO_PAYLOAD_MAP } from "../interfaces/mass-upload-grid.interface";
import moment, { Moment } from "moment";
import { HTTPWrapperContext, REQUEST_METHODS, RequestConfigImplementation, XHR_STRATEGIES } from "@ptl/xhr";
import { EnrollmentRequest, EnrollmentRequest_MN_Variation, EnrollmentRequestPayload, EthnicBackgroundOptions, MassUploadApplicant, MassUploadApplicant_MN_Variation, MassUploadCommonApplicant, MassUploadEnrollmentDependent, MassUploadEnrollmentDependent_MN_Variation, ProxiedApplicantAttestationMassUploadApplicant, ProxiedApplicantAttestationMassUploadApplicantWithProducerFullName, ProxiedSpouseAttestationMassUploadApplicant, SignatureInfo, SUBMISSION_DATE_FORMAT, SUBMISSION_STATUS, ThirdPartyPayerType, ValidEthnicBackgroundApplicant } from "../interfaces/mug-submission.interface";
import { MassUploadGridDataHelperService } from "./mug-data-helper.service";
import { BaseAgency, LINE_OF_BUSINESS_ENUM, LOGIN_INFO_REDUCER_NAME, UserInfoStateStructure } from "@ptl/account-management";
import { BrandingConfigurationService } from "@ptl/brand";
import { environment } from "src/environments/environment";
import { ReducerUtilityService } from "src/app/shared/utility-services/reducer-utility.service";
import { MUG_Subscriber_MN_Variation } from "../interfaces/mug-subscriber.interface";

interface SubscriberFormValue_ThirdPartyPayerParams {
  premiumPayment: boolean;
  payerType: string;
  dateOfLifeEvent: string;
  thirdPartyPayerName: string;
  thirdPartyPayerIdentificationNumber?: string;
}

@Injectable({
  providedIn: 'root'
})
export class MassUploadGridSubmissionDataService {


  constructor(
    private httpClient: HTTPWrapperContext,
    private gridHelper: MassUploadGridDataHelperService,
    private brandingConfigurationService: BrandingConfigurationService,
    private reducerUtilityService: ReducerUtilityService
  ) {
    
  }

  buildSubmissionPayload(gridFormMap: MassUploadStateFormMap[], agency: BaseAgency, submitStatus: SUBMISSION_STATUS): EnrollmentRequestPayload {
    
    const loginData: UserInfoStateStructure = this.reducerUtilityService.getApplicationStates()[LOGIN_INFO_REDUCER_NAME];

    /** Get producer Ids based on region */
    let producerIds: {
      producerId: string,
      producerNumber: string,
      npn: string
    };

    const npn = agency.npnNo || loginData.userInfo.npnNo;
    if (environment.initData.brand.ASPID === 'MN') {
      producerIds = {
        producerId: agency.agentACSId,
        producerNumber: agency.agentACSId,
        npn
      };
    } else {
      producerIds = {
        producerId: agency.npnNo,
        producerNumber: agency.npnNo,
        npn
      };
    }

    // convert all form groups into a EnrollmentRequestPayload object array
    const enrollmentPayloads: EnrollmentRequest[] = [];
    
    for (let gridFormIndex = 0; gridFormIndex < gridFormMap.length; gridFormIndex++) {
      const allFamilyForms = this.gridHelper.getFullFamilyFormGroupsBySubscriberGridIndex(gridFormIndex);

      // Increase index past this current family
      gridFormIndex += allFamilyForms.length - 1;

      let subscriberFormValue: any = this.getFormValue(allFamilyForms[0]) as unknown as any; // If we don't have a defined interface, let's use unknown instead of any - since it's not really any, as we're actually referencing values which seem to exist
      let dependentFormValues: MassUploadEnrollmentDependent[] = [];

      // NOTE** The payload must be sent in a specific order [sub, spouse, dependents]. This affects downstream processing.
      const spouseForm = allFamilyForms.find((form, index) => this.gridHelper.getPersonTypeByStateId(form.stateId) === EXCEL_PERSON_TYPES.SPOUSE);
      const spouseIndex = allFamilyForms.findIndex((form, index) => this.gridHelper.getPersonTypeByStateId(form.stateId) === EXCEL_PERSON_TYPES.SPOUSE);
      if (spouseIndex > 0) {
        allFamilyForms.splice(spouseIndex, 1); // remove from 'wherever'
        allFamilyForms.splice(1, 0, spouseForm); // add just after index1 ( since spouse MUST be the first 'dependent', first after subscriber )
      }

      // Then compile the dependents
      for (let dependentIndex = 1; dependentIndex < allFamilyForms.length; dependentIndex++) {
        let depFormValue = this.getFormValue(allFamilyForms[dependentIndex]) as unknown as any;

        let dependent: MassUploadEnrollmentDependent = {
          relationship: this.getMappedPersonTypeByStateId(allFamilyForms[dependentIndex].stateId),
          sameAddressAsSubscriber: depFormValue.addressLine1 ? false : true,
          firstName: depFormValue.firstName,
          lastName: depFormValue.lastName,
          middleInitial: depFormValue.middleInitial,
          addressLine1: depFormValue.addressLine1,
          addressLine2: depFormValue.addressLine2,
          city: depFormValue.city,
          zipCode: depFormValue.zipCode,
          county: depFormValue.county,
          state: depFormValue.state,
          dob: this.formatDate(depFormValue.dob),
          gender: depFormValue.gender,
          tobaccoIndicator: depFormValue.tobaccoIndicator ,
          ssn: depFormValue.ssn?.replace(/-/g, ''), // Remove dashes
          isHispanic: depFormValue.isHispanic,
          yesToHispanic: depFormValue.yesToHispanic,
          noToHispanic: depFormValue.noToHispanic,
          donateLifeRegistry: depFormValue.donateLifeRegistry,
          eSignature: subscriberFormValue.eSignatureProducer
        }

        if (environment.initData.brand.ASPID === 'MN') {
          dependent = this.attachEthnicBackground<MassUploadEnrollmentDependent_MN_Variation>(dependent as MassUploadEnrollmentDependent_MN_Variation, depFormValue);
        }

        dependentFormValues.push(dependent);
      }

      const spouseFormValue: MassUploadEnrollmentDependent = dependentFormValues.filter(d => d.relationship === EXCEL_PERSON_TYPES.SPOUSE)?.[0];
      const planRegion = this.gridHelper.getPlanRegionByStateId(allFamilyForms[0].stateId);
      
      let enrollmentObject: EnrollmentRequest = {
        portalTrackingNumber: null,
        
        marketingMaterial: subscriberFormValue.marketingMaterial,
        deductibleInfo: subscriberFormValue.deductibleInfo,
        currentCustomer: subscriberFormValue.currentCustomer,
        retainedSignedCopy: subscriberFormValue.retainedSignedCopy,
        communicationPreferenceIndicator: subscriberFormValue.communicationPreferenceIndicator,
        effectiveDate: this.formatDate(subscriberFormValue.effectiveDate),
        childOnlyPolicy: false,
        producerPhoneNumber: subscriberFormValue.producerPhoneNumber,
        submitStatus: submitStatus,
        agentInfo: {
          producerReferredAppInd: "N",
          firstName: loginData.userInfo.firstName,
          middleName: loginData.userInfo.middleInitial,
          lastName: loginData.userInfo.lastName,
          producerId: producerIds.producerId,
          producerNumber: producerIds.producerNumber,
          agencyId: loginData.userInfo?.agencyInfo.selectedAgencies[LINE_OF_BUSINESS_ENUM.INDIVIDUAL].agencyId,
          agencyNumber: agency.agencyId,
          npnNumber: producerIds.npn,
          agencyName: loginData.userInfo?.agencyInfo.selectedAgencies[LINE_OF_BUSINESS_ENUM.INDIVIDUAL].agencyName,
          email: loginData.userInfo.email,
          producerUserId: loginData.basicUserInfo.userId,
          businessPhone: loginData.userInfo.bus_phone,
          phoneNumber: loginData.userInfo.home_phone
        },
        enrollmentType: this.gridHelper.getEnrollmentTypeByStateId(allFamilyForms[0].stateId),
        planRegion: planRegion,
        hiosId: subscriberFormValue.hiosId?.attributes?.HIOS_ID,
        ddValue: this.brandingConfigurationService.getCurrentBrand().ddToken,
        ubk: this.brandingConfigurationService.getCurrentBrand().ubk,
        market: 'INDIVIDUAL',
        productType: 'Off-Exchange',
        productDescription: 'Off-Exchange',
        benefits: subscriberFormValue.benefits,
        hasOtherHealthPlan: subscriberFormValue['otherHealthPlanP&G'],
        hasOtherHealthPlanReplace: subscriberFormValue.otherHealthPlanReplace,
        agencyName: agency.agencyName,
        agencyNumber: agency.agencyId,
        producerName: subscriberFormValue.producerName,
        producerNumber: (() => {
          if (environment.initData.brand.ASPID === 'MN') {
            return agency.agentACSId;
          } else {
            return agency.npnNo;
          }
        })(),
        producerInd: !!loginData.basicUserInfo,
        
        applicant: {
          sameMailingAddress: subscriberFormValue.sameMailingAddress,
          firstName: subscriberFormValue.firstName,
          lastName: subscriberFormValue.lastName,
          middleInitial: subscriberFormValue.middleInitial,
          suffix: subscriberFormValue.suffix,
          addressLine1: subscriberFormValue.addressLine1,
          addressLine2: subscriberFormValue.addressLine2,
          city: subscriberFormValue.city,
          zipCode: subscriberFormValue.zipCode,
          county: subscriberFormValue.county,
          state: subscriberFormValue.state,
          email: subscriberFormValue.email,
          specialAssistance: subscriberFormValue.specialAssistance,
          dob: this.formatDate(subscriberFormValue.dob),
          gender: subscriberFormValue.gender,
          tobaccoIndicator: subscriberFormValue.tobaccoIndicator,
          ssn: subscriberFormValue.ssn?.replace(/-/g, ''), // Remove dashes
          disabledDependent: subscriberFormValue.disabledDependent,
          eSignatureProducer: subscriberFormValue.eSignatureProducer,

          languageSpoken: subscriberFormValue.languageSpoken,
          languageSpokenCode: (() => {
            // Populate with static value if not otherwise stated
            if (!!subscriberFormValue.languageSpoken) {
                return "7"; // always the same code no matter the answer
            }
            return null;
          })(),

          languageRead: subscriberFormValue.languageRead,
          languageReadCode: (() => {
            // Populate with static value if not otherwise stated
            if (!!subscriberFormValue.languageRead) {
                return "5"; // always the same code no matter the answer
            }
            return null;
          })(),
          
          languageWritten: subscriberFormValue.languageWritten,
          languageWrittenCode: (() => {
            // Populate with static value if not otherwise stated
            if (!!subscriberFormValue.languageWritten) {
                return "6"; // always the same code no matter the answer
            }
            return null;
          })(),

          isHispanic: subscriberFormValue.isHispanic,
          yesToHispanic: subscriberFormValue.yesToHispanic,
          noToHispanic: subscriberFormValue.noToHispanic,
          mailingAddressLine1: subscriberFormValue.mailingAddressLine1,
          mailingAddressLine2: subscriberFormValue.mailingAddressLine2,
          mailingCity: subscriberFormValue.mailingCity,
          mailingZipCode: subscriberFormValue.mailingZipCode,
          mailingState: subscriberFormValue.mailingState,
          homePhoneNumber: subscriberFormValue.homePhoneNumber,
          workPhoneNumber: subscriberFormValue.workPhoneNumber,
          mobilePhoneNumber: subscriberFormValue.mobilePhoneNumber,
          relationship: PAYLOAD_PERSON_TYPES.SUBSCRIBER,
          sameAddressAsSubscriber: null,
          dependents: dependentFormValues,
          disabilityNames: subscriberFormValue.disabilityNames,
          donateLifeRegistry: subscriberFormValue.donateLifeRegistry,
          eSignature: true,
          eSignatureFullName: this.getSignatureInfo(subscriberFormValue),
          eSignatureSpouseDomesticFullName: this.getSignatureInfo(spouseFormValue)
        },
        
        thirdPartyPayerType: this.getThirdPartyPayerInfo(subscriberFormValue),

        planDetails: subscriberFormValue.hiosId
      };

      /** MN has some "Do you live at..." vs "Do you *not* live at..." patterns */
      if (planRegion === "MINCR") {

        // Updates the MN Applicant
        const getMinnesotaApplicant = (applicant: MassUploadApplicant): MassUploadApplicant_MN_Variation => {

          let mnApplicant: MassUploadApplicant_MN_Variation = applicant as MassUploadApplicant_MN_Variation;
          const subscriberInfo = this.getFormValue(allFamilyForms[0]) as MUG_Subscriber_MN_Variation;

          /** mailingAddressExists and sameMailingAddress drive the same logic, independently - one should be boolean, the other null */
          if (!mnApplicant.mailingAddressExists) {
            mnApplicant.mailingAddressExists = null;
          }

          /** Checkbox being TRUE, means "SAME" is FALSE [ sending FALSE to payload ] */
          if (subscriberInfo.sameMailingAddressProxy) {
            mnApplicant.sameMailingAddress = false;
          } else {
            mnApplicant.sameMailingAddress = true;
          }

          if (subscriberInfo.isMailingBillingProxy) {
            mnApplicant.isMailingBilling = false;
          } else {
            mnApplicant.isMailingBilling = true; // MN ONLY FIELD
          }

          /** Phone Number */
          mnApplicant.primaryPhoneNumber = subscriberFormValue.primaryPhoneNumber;

          /** Billing Info */
          mnApplicant.billingAddressLine1 = subscriberFormValue.billingAddressLine1;
          mnApplicant.billingCity = subscriberFormValue.billingCity;
          mnApplicant.billingState = subscriberFormValue.billingState;
          mnApplicant.billingZipCode = subscriberFormValue.billingZipCode;

          /** Ethnic Background */
          mnApplicant = this.attachEthnicBackground<MassUploadApplicant_MN_Variation>(mnApplicant, subscriberFormValue);
          
          return mnApplicant;
        }
        // Updates the MN Enrollment Request
        const getMinnesotaEnrollmentObject = (request: EnrollmentRequest): EnrollmentRequest_MN_Variation => {
          const mnRequest: EnrollmentRequest_MN_Variation = request as EnrollmentRequest_MN_Variation;
          const subscriberInfo = this.getFormValue(allFamilyForms[0]) as MUG_Subscriber_MN_Variation;

          mnRequest.mnPermanentResident = subscriberInfo.mnPermanentResident;
          mnRequest.producerFullName = {
            firstName: subscriberInfo.attestationFirstNameAgent,
            lastName: subscriberInfo.attestationLastNameAgent
          }

          return mnRequest;
        }
        enrollmentObject.applicant = getMinnesotaApplicant(enrollmentObject.applicant);
        enrollmentObject = getMinnesotaEnrollmentObject(enrollmentObject);
      }

      const getOtherHealthPlanInfo = (enrollmentObject: EnrollmentRequest): EnrollmentRequest => {
        if (planRegion === "MINCR") {

          /** Single Dataset for these questions - driven by 2 controls */
          if (enrollmentObject.hasOtherHealthPlan || enrollmentObject.hasOtherHealthPlanReplace) {
            enrollmentObject.otherHealthPlan = {
              fullyConsumedBenefitsInd: subscriberFormValue.fullyConsumedBenefitsInd,
              ohpPAndGInsuranceCarrier: subscriberFormValue['ohpP&GInsuranceCarrier'],
              ohpPAndGGroupNumber: subscriberFormValue['ohpP&GGroupNumber'],
              ohpPAndGPolicyHolder: subscriberFormValue['ohpP&GPolicyHolder'],
              ohpPAndGEffectiveDate: this.formatDate(subscriberFormValue['ohpP&GEffectiveDate']),
              ohpPAndGPolicyNumber: subscriberFormValue['ohpP&GPolicyNumber'],
              ohpPAndGRelationToApplicant: subscriberFormValue['ohpP&GRelationToApplicant'],
              ohpPAndGPolicyHolderDob: this.formatDate(subscriberFormValue['ohpP&GPolicyHolderDob']),
              ohpPAndGEmploymentStatus: subscriberFormValue['ohpP&GEmploymentStatus']
            };
          }
        } else {

          /** Separate Datasets for these questions - *each* driven by a single control */
          if (enrollmentObject.hasOtherHealthPlan) {
            enrollmentObject.otherHealthPlan = {
              fullyConsumedBenefitsInd: subscriberFormValue.fullyConsumedBenefitsInd,
              ohpPAndGInsuranceCarrier: subscriberFormValue['ohpP&GInsuranceCarrier'],
              ohpPAndGGroupNumber: subscriberFormValue['ohpP&GGroupNumber'],
              ohpPAndGPolicyHolder: subscriberFormValue['ohpP&GPolicyHolder'],
              ohpPAndGEffectiveDate: this.formatDate(subscriberFormValue['ohpP&GEffectiveDate']),
              ohpPAndGPolicyNumber: subscriberFormValue['ohpP&GPolicyNumber'],
              ohpPAndGRelationToApplicant: subscriberFormValue['ohpP&GRelationToApplicant'],
              ohpPAndGPolicyHolderDob: this.formatDate(subscriberFormValue['ohpP&GPolicyHolderDob']),
              ohpPAndGEmploymentStatus: subscriberFormValue['ohpP&GEmploymentStatus']
            };
          }
            
          if (enrollmentObject.hasOtherHealthPlanReplace) {
            enrollmentObject.otherHealthPlanReplace = {
              ohpReplaceInsuranceCarrier: subscriberFormValue.ohpReplaceInsuranceCarrier,
              ohpReplaceGroupNumber: subscriberFormValue.ohpReplaceGroupNumber,
              ohpReplacePolicyHolder: subscriberFormValue.ohpReplacePolicyHolder,
              ohpReplaceEffectiveDate: this.formatDate(subscriberFormValue.ohpReplaceEffectiveDate),
              ohpReplacePolicyNumber: subscriberFormValue.ohpReplacePolicyNumber,
              ohpReplaceRelationToApplicant: subscriberFormValue.ohpReplaceRelationToApplicant,
              ohpReplacePolicyHolderDob: this.formatDate(subscriberFormValue.ohpReplacePolicyHolderDob)
            };
          }
        }
        return enrollmentObject;
      }
      enrollmentObject = getOtherHealthPlanInfo(enrollmentObject);
      
    
      enrollmentPayloads.push(enrollmentObject);
    }

    return {enrollments: enrollmentPayloads};
  }

  getThirdPartyPayerInfo(subscriberFormValue: SubscriberFormValue_ThirdPartyPayerParams): ThirdPartyPayerType {
    const obj: ThirdPartyPayerType = {
      premiumPayment: subscriberFormValue.premiumPayment,
      payerType: subscriberFormValue.payerType,
      ichra: subscriberFormValue.payerType === 'ICHRA',
      qsehra: subscriberFormValue.payerType === 'QSEHRA',
      dateOfLifeEvent: this.formatDate(subscriberFormValue.dateOfLifeEvent),
      thirdPartyPayerName: subscriberFormValue.thirdPartyPayerName,
    }

    if (subscriberFormValue.thirdPartyPayerIdentificationNumber) {
      obj.thirdPartyPayerIdentificationNumber = subscriberFormValue.thirdPartyPayerIdentificationNumber
    }

    return obj;
  }
  // Dependents but not spouses need a special type
  getMappedPersonTypeByStateId(stateId: number) {
    const personType = this.gridHelper.getPersonTypeByStateId(stateId);
    return EXCEL_TO_PAYLOAD_MAP[personType];
  }

  getFormValue(formMapEntry: MassUploadStateFormMap): unknown {
    const formKeys = Object.keys(formMapEntry.formGroups);
    let formValue: any = {};

    formKeys.forEach((formKey) => {
      Object.assign(formValue, formMapEntry.formGroups[formKey].value);
    });

    return formValue;
  }

  formatDate(date: string | Moment): string {
    if (!date) return '';

    if (moment.isMoment(date)) {
      return date.format(SUBMISSION_DATE_FORMAT);
    }

    return date;
  }

  // flatten an object into a single object of key value pairs
  flattenObject(obj: any, prefix: string = '', result: any = {}): any {
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const newKey = key;
        
        if (obj[key] !== null && typeof obj[key] === 'object' && !Array.isArray(obj[key])) {
          this.flattenObject(obj[key], newKey, result);
        } else {
          result[newKey] = obj[key];
        }
      }
    }
    return result;
  }

  getSignatureInfo(person: MassUploadCommonApplicant): SignatureInfo | null {
    if (!person) { return null }
    return {
      firstName: person.firstName,
      lastName: person.lastName
    }
  }

  attachEthnicBackground<T extends ValidEthnicBackgroundApplicant>(person: T, ethnicBackgroundOptions: EthnicBackgroundOptions) {
    person.ethnicBackground = {
      "B": ethnicBackgroundOptions.B,
      "J": ethnicBackgroundOptions.J,
      "C": ethnicBackgroundOptions.C,
      "A": ethnicBackgroundOptions.A,
      "G": ethnicBackgroundOptions.G,
      "E": ethnicBackgroundOptions.E,
      "otherEthnicBackground": ethnicBackgroundOptions.otherEthnicBackground,
      "CNA": ethnicBackgroundOptions.CNA
    }

    return person;
  }

  /**
   *
   * @param bulkEnrollmentResponse The request is sent back as part of the response - so we're just using the request I guess
   */
  handleResponseDataSignatureAttestations(bulkEnrollmentResponse: EnrollmentRequest) {
    if (environment.initData.brand.ASPID !== 'MN') { 
      return;
    }

    /**
     * ( See: "Proxy-Signature START" comment )
     */
    function replaceSubscriberSignature(e: EnrollmentRequest_MN_Variation) {
      const applicant: ProxiedApplicantAttestationMassUploadApplicant = e.applicant as ProxiedApplicantAttestationMassUploadApplicant;

      if (
        applicant.eSignatureFullName?.firstName &&
        applicant.eSignatureFullName?.lastName
      ) {
        applicant.eSignatureFullNameProxy = {
          attestationFirstNameCustomer: applicant.eSignatureFullName?.firstName,
          attestationLastNameCustomer: applicant.eSignatureFullName?.lastName
        };

        delete applicant.eSignatureFullName; // clear this so its "firstName"/"lastName" values don't get hoisted and overwrite other subscriber values
      }
    }

    /**
     * Reads the dependents on the applicant object and modify the SPOUSE with proxy formControlName values ( different from ApplicantSignature replacement because applicant is FLATTENED ( formControlNames are hoisted to the same map ), whereas DEPENDENTS are not - so we do our own hoist )
     * There is a complementary form entry replacement in mass-upload-grid.json to support this
     */
    function replaceSpouseSignature(e: EnrollmentRequest_MN_Variation) {
      const dependents: ProxiedSpouseAttestationMassUploadApplicant[] = e.applicant.dependents as unknown as ProxiedSpouseAttestationMassUploadApplicant[];

      if (dependents.length) {
        dependents
          .filter(d => d.relationship === PAYLOAD_PERSON_TYPES.SPOUSE)
          .forEach( spouse => {

            const applicant: MassUploadApplicant = e.applicant;

            if (
              applicant.eSignatureSpouseDomesticFullName?.firstName &&
              applicant.eSignatureSpouseDomesticFullName?.lastName
            ) {
              spouse.attestationFirstNameCustomer = applicant.eSignatureSpouseDomesticFullName?.firstName;
              spouse.attestationLastNameCustomer = applicant.eSignatureSpouseDomesticFullName?.lastName;
            }

            delete applicant.eSignatureSpouseDomesticFullName; // clear this so its "firstName"/"lastName" values don't get hoisted and overwrite other subscriber values

          });
      }
    }

    /**
     * Because MUG flattens applicant form controls, and hoists ALL form controls to the top level
     * We need to proxy this value, so that NOTIFICATIONS&AUTHORIZATIONS:"firstName" nor does't overwrite PERSONAL-INFO:"firstName"
     * There is a complementary form entry replacement in mass-upload-grid.json to support these attestation****NameAgent fields
     */
    function replaceAgentSignature(e: EnrollmentRequest_MN_Variation) {
      const applicant: ProxiedApplicantAttestationMassUploadApplicantWithProducerFullName = e.applicant as ProxiedApplicantAttestationMassUploadApplicantWithProducerFullName;

      if (
        e.producerFullName?.firstName &&
        e.producerFullName?.lastName
      ) {
        applicant.eSignatureProducerFullNameProxy = {
          attestationFirstNameAgent: e.producerFullName?.firstName,
          attestationLastNameAgent: e.producerFullName?.lastName
        };

      }
    }

    // Because there is an issue with duplicate formControlNames once the structure has been flattened
    // We proxy and remove the original, so there is no duplication of formControlName ( we track this by replacing the form entry itself in the entry overrides )
    replaceSubscriberSignature(bulkEnrollmentResponse as EnrollmentRequest_MN_Variation);
    replaceSpouseSignature(bulkEnrollmentResponse as EnrollmentRequest_MN_Variation);
    replaceAgentSignature(bulkEnrollmentResponse as EnrollmentRequest_MN_Variation);
  }

  /*************************************************************************** */

  async makeRequest(appId: string) {
    
    const requestConfig = new RequestConfigImplementation({
      method: REQUEST_METHODS.GET,
      contentType: 'application/octet-stream',
      accept: 'application/octet-stream',
      responseType: 'blob',
      url: '/sxesvc/api/v3/bulk-enrollment/downloadApplication',
      GET_PAYLOAD: {
        appTrackingId: appId
      }
    });

    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_POST_PASS);
    return this.httpClient.makeRequest(requestConfig);
  }

  async downloadLogByAppId(appId: string) {
    let response = await this.makeRequest(appId);
    const fileName = appId;
    this.saveByteArray(fileName, response);
  }

  saveByteArrayAsExcel(name: string, byte) {
    let blob = new Blob([byte], {type: "vnd.application/ms-excel"});
    let link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    let fileName = name;
    link.download = fileName;
    link.click();
  }

  saveByteArray(name: string, byte) {
    let blob = new Blob([byte], {type: "application/zip"});
    let link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    let fileName = name;
    link.download = fileName;
    link.click();
  }
}